import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Truck, User, CheckCircle2, Check, Loader2 } from "lucide-react";
import { apiFetch, saveAuthToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

const BASE_FIELDS = [
  ["Ragione sociale", "company_name", "Es. Trasporti Bianchi"],
  ["Partita IVA", "vat_number", "IT12345678901"],
  ["Email", "email", "nome@azienda.it"],
  ["Numero di telefono", "phone", "+39 333 1234567"],
];

const TYPES = [
  ["azienda", "Azienda / Committente", Building2],
  ["vettore", "Autotrasportatore / Vettore", Truck],
  ["autista", "Autista", User],
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    company_name: "",
    vat_number: "",
    email: "",
    phone: "",
    password: "",
    confirm: "",
    invite_code: "",
  });
  const [accountType, setAccountType] = useState(null);
  const [privacy, setPrivacy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [vatStatus, setVatStatus] = useState("idle");
  const [vatMessage, setVatMessage] = useState("");
  const [vatRegisteredName, setVatRegisteredName] = useState("");
  const [vatNameMatch, setVatNameMatch] = useState(null);
  const [vatServiceMessage, setVatServiceMessage] = useState("");

  const set = (key, value) => {
    if (key === "vat_number" || key === "company_name") {
      setVatStatus("idle");
      setVatMessage("");
      setVatRegisteredName("");
      setVatNameMatch(null);
      setVatServiceMessage("");
    }
    setForm((c) => ({ ...c, [key]: value }));
  };

  const verifyVat = async () => {
    if (accountType === "autista") return true;
    if (!form.vat_number.trim()) {
      setVatStatus("invalid");
      setVatMessage("Partita IVA non valida");
      return false;
    }
    setVatStatus("checking");
    setVatMessage("Verifica in corso...");
    try {
      const result = await apiFetch("/vat/verify", {
        method: "POST",
        body: JSON.stringify({ vat_number: form.vat_number, company_name: form.company_name }),
      });
      const nextStatus = result.status === "format_invalid" ? "invalid" : result.status;
      setVatStatus(nextStatus);
      setVatMessage(result.message);
      setVatRegisteredName(result.registered_name || "");
      setVatNameMatch(result.name_match ?? null);
      setVatServiceMessage(result.service_message || "");
      return result.status === "verified";
    } catch {
      setVatStatus("unavailable");
      setVatMessage("Impossibile verificare la Partita IVA in questo momento. Riprova più tardi.");
      setVatServiceMessage("");
      return false;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!accountType) {
      setErrorText("Scegli come vuoi registrarti per continuare.");
      return;
    }
    const requiredKeys = [
      "company_name",
      "email",
      "phone",
      "password",
      "confirm",
      ...(accountType === "autista" ? ["invite_code"] : ["vat_number"]),
    ];
    if (requiredKeys.some((k) => !(form[k] || "").trim()) || !privacy || !terms) {
      setErrorText(
        accountType === "autista"
          ? "Compila tutti i campi, inserisci il codice invito e accetta Privacy Policy e Termini."
          : "Compila tutti i campi e accetta Privacy Policy e Termini."
      );
      return;
    }
    if (form.password !== form.confirm) {
      setErrorText("Le due password devono coincidere.");
      return;
    }
    setErrorText("");
    setLoading(true);
    if (accountType !== "autista" && !(await verifyVat())) {
      setLoading(false);
      return;
    }
    try {
      const result = await apiFetch("/auth/register", {
        method: "POST",
        body: JSON.stringify({
          company_name: form.company_name,
          vat_number: accountType === "autista" ? "" : form.vat_number,
          email: form.email,
          phone: form.phone,
          password: form.password,
          account_type: accountType,
          privacy_accepted: privacy,
          terms_accepted: terms,
          invite_code: accountType === "autista" ? form.invite_code.trim() : undefined,
        }),
      });
      saveAuthToken(result.token);
      await refresh();
      navigate("/", { replace: true });
    } catch (error) {
      setErrorText(error.message || "Registrazione non riuscita. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "min-h-[52px] w-full rounded-xl border border-border bg-card px-4 text-base text-foreground outline-none focus:border-primary";

  const Checkbox = ({ id, label, linkText, linkHref, value, onPress }) => (
    <div className="flex min-h-[44px] items-center gap-2.5">
      <button
        type="button"
        data-testid={`register-checkbox-${id}`}
        onClick={onPress}
        className="flex flex-1 items-center gap-2.5"
      >
        <span
          className={cn(
            "flex h-6 w-6 items-center justify-center rounded-md border-2",
            value ? "border-primary bg-primary" : "border-muted-foreground bg-card"
          )}
        >
          {value ? <Check className="h-4 w-4 text-primary-foreground" /> : null}
        </span>
        <span className="text-sm text-card-foreground">{label}</span>
      </button>
      <Link
        to={linkHref}
        data-testid={`register-${id}-link`}
        className="py-2 text-sm font-extrabold text-primary underline"
      >
        {linkText}
      </Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto w-full max-w-xl px-6 py-6">
        <Link to="/login" className="mb-4 flex items-center gap-2 text-foreground">
          <ArrowLeft className="h-5 w-5" />
          <span className="font-semibold">Indietro</span>
        </Link>
        <img src="/cargoflow-logo.jpg" alt="CargoFlow" className="mx-auto h-16 w-auto object-contain" />
        <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground">Crea il tuo account</h1>
        <p className="mt-2 text-[15px] text-muted-foreground">
          Inizia a gestire i tuoi trasporti con CargoFlow.
        </p>

        <h2 className="mt-6 text-lg font-extrabold text-foreground">Come vuoi registrarti?</h2>
        <div className="mt-3 space-y-2.5">
          {TYPES.map(([value, label, Icon]) => {
            const selected = accountType === value;
            return (
              <button
                key={value}
                type="button"
                data-testid={`register-type-${value}`}
                onClick={() => setAccountType(value)}
                className={cn(
                  "flex min-h-[64px] w-full items-center gap-3 rounded-2xl border-[1.5px] p-4 text-left",
                  selected ? "border-primary bg-secondary" : "border-border bg-card"
                )}
              >
                <Icon className={cn("h-6 w-6", selected ? "text-primary" : "text-muted-foreground")} />
                <span className="flex-1 text-sm font-bold text-foreground">{label}</span>
                {selected ? <CheckCircle2 className="h-5 w-5 text-primary" /> : null}
              </button>
            );
          })}
        </div>

        {!accountType ? (
          <p data-testid="register-hint" className="mt-4 text-[15px] text-muted-foreground">
            Seleziona il tipo di account: ti mostreremo solo i campi necessari.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-2">
            <h2 className="mt-6 mb-2 text-lg font-extrabold text-foreground">
              {accountType === "autista" ? "I tuoi dati" : "Dati aziendali"}
            </h2>
            {BASE_FIELDS.filter(([, key]) => accountType !== "autista" || key !== "vat_number").map(
              ([label, key, placeholder]) => (
                <div key={key} className="mt-2.5">
                  <label className="mb-1.5 block text-[13px] font-semibold text-card-foreground">
                    {key === "company_name" && accountType === "autista" ? "Nome e cognome" : label}
                  </label>
                  <input
                    data-testid={`register-field-${key}`}
                    type={key === "email" ? "email" : "text"}
                    placeholder={placeholder}
                    value={form[key]}
                    onChange={(e) => set(key, e.target.value)}
                    className={inputClass}
                  />
                  {key === "vat_number" && accountType !== "autista" ? (
                    <div className="mt-2 space-y-1">
                      {vatMessage ? (
                        <p
                          data-testid="register-vat-status"
                          className={cn(
                            "text-[13px] font-bold",
                            vatStatus === "verified"
                              ? "text-[hsl(var(--success))]"
                              : vatStatus === "checking"
                                ? "text-muted-foreground"
                                : "text-destructive"
                          )}
                        >
                          {vatMessage}
                        </p>
                      ) : null}
                      {vatServiceMessage ? (
                        <p className="text-xs text-[hsl(var(--warning))]">{vatServiceMessage}</p>
                      ) : null}
                      {vatStatus === "verified" && vatRegisteredName ? (
                        <p className="text-xs text-muted-foreground">
                          Ragione sociale registrata: {vatRegisteredName}
                        </p>
                      ) : null}
                      {vatStatus === "verified" && vatNameMatch === false ? (
                        <p className="text-xs text-[hsl(var(--warning))]">
                          Attenzione: la ragione sociale inserita non coincide con quella registrata.
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )
            )}
            {accountType === "autista" ? (
              <div className="mt-2.5">
                <label className="mb-1.5 block text-[13px] font-semibold text-card-foreground">
                  Codice invito del vettore
                </label>
                <input
                  data-testid="register-field-invite_code"
                  placeholder="Es. A1B2C3"
                  value={form.invite_code}
                  onChange={(e) => set("invite_code", e.target.value.toUpperCase())}
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Te lo fornisce il vettore dalla sezione "Autisti".
                </p>
              </div>
            ) : null}

            <h2 className="mt-7 mb-2 text-lg font-extrabold text-foreground">Sicurezza</h2>
            {[
              ["Password", "password"],
              ["Conferma password", "confirm"],
            ].map(([label, key]) => (
              <div key={key} className="mt-2.5">
                <label className="mb-1.5 block text-[13px] font-semibold text-card-foreground">{label}</label>
                <input
                  data-testid={`register-field-${key}`}
                  type="password"
                  placeholder="Minimo 8 caratteri"
                  value={form[key]}
                  onChange={(e) => set(key, e.target.value)}
                  className={inputClass}
                />
              </div>
            ))}

            <div className="mt-6 space-y-3">
              <Checkbox id="privacy" label="Accetto la" linkText="Privacy Policy" linkHref="/privacy" value={privacy} onPress={() => setPrivacy(!privacy)} />
              <Checkbox id="terms" label="Accetto" linkText="Termini e Condizioni" linkHref="/terms" value={terms} onPress={() => setTerms(!terms)} />
            </div>

            {errorText ? (
              <p data-testid="register-error-text" className="mt-3 text-sm font-semibold text-destructive">
                {errorText}
              </p>
            ) : null}

            <button
              type="submit"
              data-testid="register-submit"
              disabled={loading}
              className="mt-7 flex min-h-[54px] w-full items-center justify-center gap-2 rounded-2xl bg-primary text-sm font-extrabold tracking-wide text-primary-foreground hover:opacity-90 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
              {loading ? "Creazione account..." : "REGISTRATI"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
